import { BadgeCardRow } from '@bp/ui-components';
import dayjs from 'dayjs';
import { CardType } from '../components/TimetableVersion/graphql/types';
import {
  _ClassesQuery,
  _LessonsDocument,
  _LessonsQuery,
  _LessonsQueryVariables,
  _PeopleQuery,
  _TeachingBlockCardsDocument,
  _TeachingBlockCardsQuery,
  _TeachingBlockCardsQueryVariables,
  _TeachingBlocksDocument,
  _TeachingBlocksQuery,
  _TeachingBlocksQueryVariables,
  _TimeGridEntriesDocument,
  _TimeGridEntriesQuery,
  _TimeGridEntriesQueryVariables,
  _TimetableCardsDocument,
  _TimetableCardsQuery,
  _TimetableCardsQueryVariables,
  _TimetablesDocument,
  _TimetablesQuery,
  _TimetablesQueryVariables,
  ClassGridCardsDocument,
  ClassGridCardsQuery,
  ClassGridCardsQueryVariables,
} from '../types/planung-graphql-client-defs';
import { createIntArray, uniqueBy } from './arrayFunc';
import { loadBasicData } from './loadBasicData';
import { urqlClient } from './urqlClient';

export const getTimetableCardType = async (versionUuid: string, schoolyearUuid: string) => {
  const { subjectData, classesData, teacherData, groupsData, roomsData, subjectContainerData } = await loadBasicData({
    schoolyearUuid: schoolyearUuid,
  });

  const [timetableData, lessonsQueryData, data] = await Promise.all([
    urqlClient
      .query<_TimetablesQuery, _TimetablesQueryVariables>(
        _TimetablesDocument,
        {
          where: {
            versions_SOME: { uuid: versionUuid },
          },
        },

        { requestPolicy: 'network-only' },
      )
      .toPromise(),
    urqlClient
      .query<_LessonsQuery, _LessonsQueryVariables>(
        _LessonsDocument,
        {
          where: {
            versions_SOME: { uuid: versionUuid },
          },
        },
        { requestPolicy: 'network-only' },
      )
      .toPromise(),
    urqlClient
      .query<_TimetableCardsQuery, _TimetableCardsQueryVariables>(
        _TimetableCardsDocument,
        {
          where: {
            timetableVersion: { uuid: versionUuid },
            lesson: {
              versions_SOME: { uuid: versionUuid },
            },
          },
        },
        { requestPolicy: 'network-only' },
      )
      .toPromise(),
  ]);

  const timegridUuid = timetableData?.data?.timetables[0]?.timegridConfigsConnection.edges[0]?.node.timegrid?.uuid;
  const subjectContainerUuids = data?.data?.timetableCards.map((card) => card.subject.uuid) ?? [];

  const { data: teachingBlockCardsData } = await urqlClient
    .query<_TeachingBlockCardsQuery, _TeachingBlockCardsQueryVariables>(
      _TeachingBlockCardsDocument,
      {
        where: {
          teachingBlockVersion: {
            active: true,
            teachingBlock: {
              subjectContainer: {
                uuid_IN: subjectContainerUuids,
              },
            },
          },
        },
      },
      { requestPolicy: 'network-only' },
    )
    .toPromise();

  const { data: teachingBlockData } = await urqlClient
    .query<_TeachingBlocksQuery, _TeachingBlocksQueryVariables>(_TeachingBlocksDocument, {
      where: {
        subjectContainer: {
          uuid_IN: subjectContainerUuids,
        },
      },
    })
    .toPromise();

  const { data: timeGridEntriesData } = await urqlClient
    .query<_TimeGridEntriesQuery, _TimeGridEntriesQueryVariables>(_TimeGridEntriesDocument, {
      where: {
        timeGrid: {
          uuid: timegridUuid,
        },
      },
    })
    .toPromise();

  const { data: classCardsData } = await urqlClient
    .query<ClassGridCardsQuery, ClassGridCardsQueryVariables>(
      ClassGridCardsDocument,
      {
        where: { timetableVersion: { uuid: versionUuid } },
      },
      { requestPolicy: 'network-only' },
    )
    .toPromise();

  const cards: CardType[] =
    data?.data?.timetableCards.map((card): CardType => {
      const lesson = lessonsQueryData?.data?.lessons.find((lesson) => lesson.uuid === card?.lesson.uuid);

      const lessonClasses =
        lesson?.lessonClassesConnection.edges.map((edge) => {
          return edge.node;
        }) ?? [];

      const classes = lessonClasses
        .map((lc) => {
          return classesData?.data?.classes.find((c) => c.uuid === lc?.class.uuid);
        })
        .sort((a, b) => {
          return a?.name.localeCompare(b?.name ?? '') ?? 0;
        });

      const teachers = lesson?.teachersConnection.edges.map((t) => {
        return (
          teacherData?.data?.people.find((p) => p.uuid === t.node.uuid) ??
          ({} as Pick<_PeopleQuery, 'people'>['people'][0])
        );
      });

      const rooms = roomsData?.rooms.filter((room) => card.rooms.some((r) => r.uuid === room.uuid));

      const cardClasses = classes.map((c) => c?.uuid);
      const subject = subjectData?.data?.subjects.find((subject) => subject.uuid === card.subject.uuid);
      const subjectContainer = subjectContainerData?.data?.subjectContainers.find(
        (sc) => sc.uuid === card.subject.uuid,
      );

      const teachingBlockTeachers: Pick<_PeopleQuery, 'people'>['people'] = [];
      const teachingBlockRooms: { uuid: string; name: string }[] = [];

      if (subjectContainer) {
        const teachingBlock = teachingBlockData?.teachingBlocks.find(
          (tb) => tb.subjectContainer.uuid === subjectContainer.uuid,
        );
        const version = teachingBlock?.versions.find((version) => version.active);
        const teachingBlockCards = teachingBlockCardsData?.teachingBlockCards.filter((tbc) => {
          const teachingBlockCardClasses = tbc.lessonClasses.map((lc) => lc.class.uuid);
          return (
            tbc.teachingBlockVersion.uuid === version?.uuid &&
            teachingBlockCardClasses.some((c) => cardClasses.includes(c))
          );
        });
        teachingBlockCards?.forEach((tbc) => {
          const teacherUuids = tbc.teachers.map((t) => t.uuid);
          const teachers = teacherData?.data?.people.filter((p) => teacherUuids.includes(p.uuid));
          if (teachers) {
            teachingBlockTeachers.push(...teachers);
          }
          if (tbc.startDate && tbc.endDate) {
            const _rooms = tbc.rooms
              .map((r) => {
                return roomsData?.rooms.find((room) => room.uuid === r.uuid);
              })
              .filter((r) => r) as { uuid: string; name: string }[];
            if (_rooms && _rooms.length > 0) {
              teachingBlockRooms.push(..._rooms);
            }
          }
        });
      }

      const classGridCards =
        classCardsData?.classGridCards.filter((classGridCard) => {
          return classGridCard.timetableCardUuid === card?.uuid;
        }) ?? [];

      const groupUuids = lessonClasses.map((lc) => {
        return lc?.groups.map((g) => g.uuid);
      });
      const groups = groupsData?.data?.groups.filter((group) => {
        return groupUuids.some((groupUuid) => groupUuid?.includes(group.uuid)) ?? false;
      });

      const postions: number[] = [];
      classGridCards?.forEach((c) => {
        if ((c.divisionsCount ?? 1) > 1) {
          postions.push(c.usedPosition ?? 1);
        } else {
          postions.push(0);
        }
      });

      const start = timeGridEntriesData?.timeGridEntries.find((entry) => entry.uuid === card.startTimeGridEntry?.uuid);
      const startTime = start ? dayjs(start.start).format('HH:mm') : null;

      const end = timeGridEntriesData?.timeGridEntries.find((entry) => entry.uuid === card.endTimeGridEntry?.uuid);
      const endTime = end ? dayjs(end.end).format('HH:mm') : null;
      const cardWithBiggestDivisionsCount = classGridCards.find(
        (c) => c.divisionsCount === Math.max(...classGridCards.map((c) => c.divisionsCount ?? 1)),
      );
      const rows: BadgeCardRow[] = createIntArray(cardWithBiggestDivisionsCount?.divisionsCount ?? 1).map((i) => {
        if (postions.includes(i)) {
          return {
            rowColor: '',
            colColors: cardWithBiggestDivisionsCount?.colors ?? [],
          };
        }
        return {
          rowColor: '',
          colColors: [],
        };
      });

      return {
        ...card,
        start: card.startTimeGridEntry?.uuid ?? null,
        end: card.endTimeGridEntry?.uuid ?? null,
        startTime: startTime,
        endTime: endTime,
        subject: subjectContainer ? subjectContainer : subject,
        isSubjectContainer: !!subjectContainer,
        lesson: lesson,
        classGridCards: classGridCards,
        lessonClasses: lessonClasses,
        classes: (classes.filter((c) => c) as Pick<_ClassesQuery, 'classes'>['classes']) ?? [],
        teachers: subjectContainer ? uniqueBy(teachingBlockTeachers, 'uuid') : (teachers ?? []),
        groups: groups ?? [],
        rooms: subjectContainer ? uniqueBy(teachingBlockRooms, 'uuid') : (rooms ?? []),
        badgeCardRows: rows,
        badgeCardTextColor: cardWithBiggestDivisionsCount?.textColor ?? '',
      };
    }) ?? [];

  return cards;
};
